import ShortcutIcons from '@src/svgs/ShortcutIcon';
import { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import Modal from '../common/Modal';
import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';

type ShortcutType = {
  command: string;
  macOs: string;
  windows: string;
};

const SHORTCUTS: ShortcutType[] = [
  { command: 'Timeline Zoom In', macOs: '+', windows: '+' },
  { command: 'Timeline Zoom Out', macOs: '-', windows: '-' },
  { command: 'Fit Timeline to Screen', macOs: '/', windows: '/' },
  { command: 'Split Element', macOs: 'Cmd + K', windows: 'Ctrl + K' },
  {
    command: 'Play / Pause Video',
    macOs: 'Spacebar or K',
    windows: 'Spacebar or K',
  },
  {
    command: 'Delete Element / Word',
    macOs: 'Delete or Backspace',
    windows: 'Delete or Backspace',
  },
  {
    command: 'Jump to Start / End of Video',
    macOs: 'Fn + Left Arrow / Fn + Right Arrow',
    windows: 'Fn + Left Arrow / Fn + Right Arrow',
  },
  { command: 'Save', macOs: 'Cmd + S', windows: 'Ctrl + S' },
  {
    command: 'Undo / Redo',
    macOs: 'Cmd + Z / Cmd + Y',
    windows: 'Ctrl + Z / Ctrl + Y',
  },
  {
    command: 'Move Frame-by-Frame (Timeline)',
    macOs: 'Left Arrow (←) / Right Arrow (→)',
    windows: 'Left Arrow (←) / Right Arrow (→)',
  },
  {
    command: 'Next Edit Point',
    macOs: 'Up Arrow (↑)',
    windows: 'Up Arrow (↑)',
  },
  {
    command: 'Previous Edit Point',
    macOs: 'Down Arrow (↓)',
    windows: 'Down Arrow (↓)',
  },
];

export const ShortcutsModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Main
      onClick={() => setIsOpen(true)}
      data-tooltip-id="shortcuts-modal-tooltip"
      data-tooltip-content="Keyboard shortcuts"
    >
      <ShortcutIcons
        width="18"
        height="18"
        strokeColor="currentColor"
        style={{ paddingTop: '2px' }}
      />
      <Tooltip
        id="shortcuts-modal-tooltip"
        style={COMPACT_TOOLTIP_STYLE}
        place="bottom"
        positionStrategy="fixed"
      />
      <Modal isOpen={isOpen} closeModal={() => setIsOpen(false)}>
        <Wrapper>
          <ModalHeading>Keyboard Shortcuts</ModalHeading>
          <Table>
            <thead>
              <tr>
                <TableHeader>Commands</TableHeader>
                <TableHeader>macOS</TableHeader>
                <TableHeader>Windows</TableHeader>
              </tr>
            </thead>
            <TableBody>
              {SHORTCUTS.map(shortcut => (
                <tr key={shortcut.command}>
                  <TableCell>{shortcut.command}</TableCell>
                  <TableCell>{shortcut.macOs}</TableCell>
                  <TableCell>{shortcut.windows}</TableCell>
                </tr>
              ))}
            </TableBody>
          </Table>
        </Wrapper>
      </Modal>
    </Main>
  );
};

const Main = styled.div<{ active?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: var(--radius-button);
  color: var(--foreground);
  background-color: transparent;
  cursor: pointer;
  transition: all 150ms ease-out;

  &:hover {
    background-color: var(--surface-elevated);
  }

  &:active {
    background-color: var(--gray-700);
    scale: 0.98;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 48px 24px;
  border-radius: 16px;
  border: 1px solid var(--border);
  box-sizing: border-box;
  background: var(--background);
  color: var(--text-secondary);
`;

const ModalHeading = styled.div`
  color: var(--text-secondary);
  text-align: start;
  font-family: var(--font-primary);
  font-size: 32px;
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: 120%;
  margin: 0;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  margin: 0px 20px;
`;

const TableBody = styled.tbody`
  & tr:not(:last-child) {
    border-bottom: 0.5px solid var(--border);
  }
`;

const TableCell = styled.td`
  padding: 12px;
  text-align: start;

  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-style: normal;
  font-weight: var(--font-weight-medium);
  line-height: 20px;
  letter-spacing: 0;
`;

const TableHeader = styled.th`
  background-color: rgba(72, 72, 72, 0.5);
  text-align: start;
  padding: 12px;
  border-left: none;
  border-right: none;

  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-style: normal;
  font-weight: var(--font-weight-medium);
  line-height: 18px;
  letter-spacing: 0;
`;
